import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import EodCandleChart from '../components/eod/EodCandleChart';
import {
  DEFAULT_TOGGLES, SETTINGS_KEY, calcFromDate, aggregateOHLCV,
  discoverFOReversalLevels, discoverEQReversalLevels, fmtVol, fmtFullDate,
  resolvePartRow, buildPartHoverHtml,
} from '../utils/eodChartMath';
import '../styles/eod-chart.css';

const API = '/api/api_idx_chart.php';
const PART_API = '/api/api_eod_participant.php';
const PERIODS = ['5D', '1W', '1M', '3M', '6M', '1Y', '3Y', '5Y', 'ALL'];
const TFS = ['D', 'W', 'M'];

const TOGGLE_BTNS = [
  { k: 'ema20', label: 'EMA20' },
  { k: 'ema50', label: 'EMA50' },
  { k: 'ema100', label: 'EMA100' },
  { k: 'ema200', label: 'EMA200' },
  { k: 'lrc', label: 'LRC' },
  { k: 'atr217', label: 'ATR 21×7' },
  { k: 'atr103', label: 'ATR 10×3' },
  { k: 'fib', label: 'FIB' },
  { k: 'cprM', label: 'CPR-M', title: 'Monthly CPR' },
  { k: 'cprW', label: 'CPR-W', title: 'Weekly CPR' },
  { k: 'monthLines', label: 'MTH' },
  { k: 'svwap', label: 'SVWAP', title: 'Series VWAP' },
  { k: 'psvwap', label: 'PSVWAP', title: 'Prev Series VWAP' },
  { k: 'foScore', label: 'FO Score', title: 'FO Swing Score' },
  { k: 'eqScore', label: 'EQ Score', title: 'EQ Swing Score' },
  { k: 'partSig', label: 'PART', title: 'Participant Data on hover — Client · FII · Pro · DII (Index/Stock Futures net + Δ)' },
];

function loadSettings() {
  try {
    const s = localStorage.getItem(SETTINGS_KEY);
    if (s) return JSON.parse(s);
  } catch (_) {}
  return null;
}

async function apiFetch(action, params = {}) {
  const url = `${API}?${new URLSearchParams({ action, ...params })}`;
  const res = await fetch(url, { credentials: 'include' });
  return res.json();
}

export default function EodChartPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const saved = loadSettings();

  const [symbols, setSymbols] = useState({ indices: [], stocks: [] });
  const [symbol, setSymbol] = useState(searchParams.get('symbol') || 'NIFTY');
  const [period, setPeriod] = useState(saved?.period || '6M');
  const [tf, setTf] = useState(saved?.tf || 'D');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [toggles, setToggles] = useState({ ...DEFAULT_TOGGLES, ...(saved?.tg || {}) });

  const [viewRaw, setViewRaw] = useState([]);
  const [fullRaw, setFullRaw] = useState([]);
  const [meta, setMeta] = useState({});
  const [markers, setMarkers] = useState({});
  const [foCache, setFoCache] = useState(null);
  const [eqCache, setEqCache] = useState(null);
  const [partSignals, setPartSignals] = useState([]);
  const [partSeriesByDate, setPartSeriesByDate] = useState(new Map());
  const [cprMonthly, setCprMonthly] = useState([]);
  const [cprWeekly, setCprWeekly] = useState([]);
  const [foReversal, setFoReversal] = useState(null);
  const [eqReversal, setEqReversal] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hoverIdx, setHoverIdx] = useState(-1);
  const [hoverHtml, setHoverHtml] = useState('');

  const fullData = useMemo(() => aggregateOHLCV(fullRaw, tf), [fullRaw, tf]);
  const viewData = useMemo(() => aggregateOHLCV(viewRaw, tf), [viewRaw, tf]);
  const dates = useMemo(() => viewData.map(d => d.date), [viewData]);

  const foScores = useMemo(() => {
    if (!foCache?.length) return [];
    const m = new Map(foCache.map(r => [r.date, r.foScore]));
    return dates.map(d => m.get(d) ?? null);
  }, [foCache, dates]);

  const eqScores = useMemo(() => {
    if (!eqCache?.data?.length) return [];
    const m = new Map(eqCache.data.map(r => [r.date, r.eqScore]));
    return dates.map(d => m.get(d) ?? null);
  }, [eqCache, dates]);

  const saveSettings = useCallback((tg, p, t) => {
    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify({ tg, tf: t, period: p }));
    } catch (_) {}
  }, []);

  const applyPeriodDates = useCallback((p) => {
    const d = calcFromDate(p);
    setFrom(d.from);
    setTo(d.to);
  }, []);

  useEffect(() => {
    document.querySelector('.tmr-main')?.classList.add('ic-pg');
    return () => document.querySelector('.tmr-main')?.classList.remove('ic-pg');
  }, []);

  useEffect(() => {
    applyPeriodDates(period);
    apiFetch('symbols').then(res => {
      setSymbols({ indices: res.indices || [], stocks: res.stocks || [] });
    }).catch(() => {});
  }, [applyPeriodDates, period]);

  const fetchFO = useCallback(async (sym) => {
    const res = await apiFetch('fodata', { idx: sym });
    if (res.data?.length) {
      setFoCache(res.data);
      return res.data;
    }
    return null;
  }, []);

  const fetchEQ = useCallback(async (sym) => {
    const res = await apiFetch('eqscoredata', { idx: sym });
    if (res.data?.length) {
      setEqCache({ data: res.data, maxScore: res.maxScore || 20 });
      return res.data;
    }
    return null;
  }, []);

  const fetchCPR = useCallback(async (sym, type) => {
    const res = await apiFetch('cprdata', { idx: sym, type });
    const levels = res.levels || [];
    if (type === 'monthly') setCprMonthly(levels);
    else setCprWeekly(levels);
    return levels;
  }, []);

  const fetchPartSignals = useCallback(async (scope, rangeFrom, rangeTo) => {
    if (!rangeFrom || !rangeTo) {
      setPartSignals([]);
      setPartSeriesByDate(new Map());
      return [];
    }
    try {
      const url = `${PART_API}?${new URLSearchParams({
        action: 'chart_signals',
        from: rangeFrom,
        to: rangeTo,
        scope,
      })}`;
      const res = await fetch(url, { credentials: 'include' });
      const json = await res.json();
      const sigs = json.signals || [];
      const series = json.series || [];
      setPartSignals(sigs);
      const sm = new Map();
      series.forEach((row) => {
        if (row?.date) sm.set(row.date, row);
      });
      // Fallback: if an older API has no series, synthesize from signal days only
      if (!sm.size) {
        sigs.forEach((s) => {
          const row = { date: s.date };
          (s.participants || []).forEach((p) => { row[p.client] = p; });
          sm.set(s.date, row);
        });
      }
      setPartSeriesByDate(sm);
      return sigs;
    } catch (_) {
      setPartSignals([]);
      setPartSeriesByDate(new Map());
      return [];
    }
  }, []);

  const loadChart = useCallback(async (dateOverride) => {
    if (!symbol) return;
    setLoading(true);
    setError(null);
    setFoCache(null);
    setEqCache(null);
    setPartSignals([]);
    setPartSeriesByDate(new Map());
    setCprMonthly([]);
    setCprWeekly([]);
    setFoReversal(null);
    setEqReversal(null);

    const useFrom = dateOverride?.from ?? from;
    const useTo = dateOverride?.to ?? to;

    try {
      const paramsView = { idx: symbol };
      if (useFrom) paramsView.from = useFrom;
      if (useTo) paramsView.to = useTo;

      const [resView, resFull] = await Promise.all([
        apiFetch('chartdata', paramsView),
        apiFetch('chartdata', { idx: symbol }),
      ]);

      if (resView.error) throw new Error(resView.error);
      if (!resView.data?.length) throw new Error('No data found for this period');

      setViewRaw(resView.data);
      setFullRaw(resFull.data?.length ? resFull.data : resView.data);
      setMeta(resView.meta || {});
      setMarkers(resView.markers || {});

      // Participant OI is market-wide (index vs stock futures), not per-symbol.
      // INDEX chart → Index Futures Fresh Long/Short; STOCK chart → Stock Futures.
      const metaType = String(resView.meta?.type || resView.meta?.stype || '').toUpperCase();
      const partScope = (metaType === 'STOCK' || metaType === 'EQ') ? 'stock' : 'index';

      const dataFrom = resView.data[0]?.date || useFrom;
      const dataTo = resView.data[resView.data.length - 1]?.date || useTo;

      const extras = [fetchFO(symbol), fetchEQ(symbol), fetchPartSignals(partScope, dataFrom, dataTo)];
      if (toggles.cprM) extras.push(fetchCPR(symbol, 'monthly'));
      if (toggles.cprW) extras.push(fetchCPR(symbol, 'weekly'));
      await Promise.all(extras);
    } catch (e) {
      setError(e.message);
      setViewRaw([]);
      setFullRaw([]);
    } finally {
      setLoading(false);
    }
  }, [symbol, from, to, toggles, fetchFO, fetchEQ, fetchCPR, fetchPartSignals]);

  useEffect(() => {
    if (symbol) {
      const d = calcFromDate(period);
      loadChart(d);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (fullRaw.length && foCache) setFoReversal(discoverFOReversalLevels(foCache, fullRaw));
  }, [fullRaw, foCache]);

  useEffect(() => {
    if (fullRaw.length && eqCache) setEqReversal(discoverEQReversalLevels(eqCache, fullRaw));
  }, [fullRaw, eqCache]);

  const onSymbolChange = (sym) => {
    setSymbol(sym);
    setSearchParams({ symbol: sym });
  };

  const onPeriod = (p) => {
    setPeriod(p);
    const d = calcFromDate(p);
    setFrom(d.from);
    setTo(d.to);
    saveSettings(toggles, p, tf);
    loadChart(d);
  };

  const onTf = (t) => {
    setTf(t);
    saveSettings(toggles, period, t);
  };

  const onToggle = async (k) => {
    const next = { ...toggles, [k]: !toggles[k] };
    setToggles(next);
    saveSettings(next, period, tf);
    if (next[k] && (k === 'cprM') && !cprMonthly.length) await fetchCPR(symbol, 'monthly');
    if (next[k] && (k === 'cprW') && !cprWeekly.length) await fetchCPR(symbol, 'weekly');
    if (next[k] && (k === 'svwap' || k === 'psvwap' || k === 'foScore') && !foCache) await fetchFO(symbol);
    if (next[k] && k === 'eqScore' && !eqCache) await fetchEQ(symbol);
    if (next[k] && k === 'partSig' && !partSignals.length && viewRaw.length) {
      const st = String(meta?.stype || meta?.type || '').toUpperCase();
      const majorIdx = ['NIFTY', 'BANKNIFTY', 'FINNIFTY', 'MIDCPNIFTY', 'SENSEX', 'BANKEX'].includes(symbol);
      const partScope = (st === 'STOCK' || st === 'EQ') ? 'stock'
        : (st === 'INDEX' || st === 'IDX' || majorIdx) ? 'index' : 'stock';
      await fetchPartSignals(partScope, viewRaw[0]?.date, viewRaw[viewRaw.length - 1]?.date);
    }
  };

  const buildHoverHtml = useCallback((idx, bar) => {
    if (!bar) return '';
    const bull = bar.close >= bar.open;
    const cl = bull ? 'var(--green)' : 'var(--red)';
    const prev = idx > 0 ? viewData[idx - 1] : bar;
    const chg = bar.close - prev.close;
    const chgPct = prev.close ? (chg / prev.close) * 100 : 0;

    let html = `
      <span><span class="ol">DATE</span> <b>${fmtFullDate(bar.date)}</b></span>
      <span><span class="ol">TF</span> <b>${tf}</b></span>
      <span><span class="ol">O</span> <b style="color:${cl}">${(+bar.open).toFixed(2)}</b></span>
      <span><span class="ol">H</span> <b style="color:${cl}">${(+bar.high).toFixed(2)}</b></span>
      <span><span class="ol">L</span> <b style="color:${cl}">${(+bar.low).toFixed(2)}</b></span>
      <span><span class="ol">C</span> <b style="color:${cl}">${(+bar.close).toFixed(2)}</b></span>
      <span><span class="ol">V</span> <b>${fmtVol(bar.volume)}</b></span>
      <span><span class="ol">CHG</span> <b style="color:${chg >= 0 ? 'var(--green)' : 'var(--red)'}">${chg >= 0 ? '+' : ''}${chg.toFixed(2)} (${chgPct.toFixed(2)}%)</b></span>
    `;

    if (foScores[idx] != null && toggles.foScore) {
      const fs = foScores[idx];
      const obLv = foReversal?.obLevel ?? 17;
      const osLv = foReversal?.osLevel ?? 4;
      const mid = Math.round((obLv + osLv) / 2);
      let fCol = 'var(--text-dim)', fLbl = '—';
      const p1 = idx >= 1 ? foScores[idx - 1] : null;
      const p2 = idx >= 2 ? foScores[idx - 2] : null;
      if (fs >= obLv && p1 != null && p1 >= obLv && p2 != null && p2 >= obLv) { fCol = '#FF00FF'; fLbl = 'Greed 3D'; }
      else if (fs >= obLv) { fCol = '#EC407A'; fLbl = 'Greed'; }
      else if (fs <= osLv && p1 != null && p1 <= osLv && p2 != null && p2 <= osLv) { fCol = '#00FF41'; fLbl = 'Fear 3D'; }
      else if (fs <= osLv) { fCol = '#39FF14'; fLbl = 'Fear'; }
      else if (fs > mid) { fCol = '#7CFC00'; fLbl = 'Bullish'; }
      else { fCol = 'var(--red)'; fLbl = 'Bearish'; }
      html += `<span><span class="ol" style="color:#FF9800">FO</span> <b style="color:${fCol}">${fs}/20 ${fLbl}</b></span>`;
    }

    if (eqScores[idx] != null && toggles.eqScore) {
      const es = eqScores[idx];
      const eqMaxS = eqReversal?.maxScore ?? eqCache?.maxScore ?? 20;
      const eqOB = eqReversal?.obLevel ?? (eqMaxS === 12 ? 10 : 17);
      const eqOS = eqReversal?.osLevel ?? (eqMaxS === 12 ? 3 : 4);
      const eqMid = Math.round((eqOB + eqOS) / 2);
      let eCol = 'var(--text-dim)', eLbl = '—';
      const p1 = idx >= 1 ? eqScores[idx - 1] : null;
      const p2 = idx >= 2 ? eqScores[idx - 2] : null;
      if (es >= eqOB && p1 != null && p1 >= eqOB && p2 != null && p2 >= eqOB) { eCol = '#FF00FF'; eLbl = 'Greed 3D'; }
      else if (es >= eqOB) { eCol = '#EC407A'; eLbl = 'Greed'; }
      else if (es <= eqOS && p1 != null && p1 <= eqOS && p2 != null && p2 <= eqOS) { eCol = '#00FF41'; eLbl = 'Fear 3D'; }
      else if (es <= eqOS) { eCol = '#39FF14'; eLbl = 'Fear'; }
      else if (es > eqMid) { eCol = '#7CFC00'; eLbl = 'Bullish'; }
      else { eCol = 'var(--red)'; eLbl = 'Bearish'; }
      html += `<span><span class="ol" style="color:#42A5F5">EQ</span> <b style="color:${eCol}">${es}/${eqMaxS} ${eLbl}</b></span>`;
    }

    // Client · FII · Pro · DII — net + day-change on every bar (not only Fresh Long/Short days)
    if (toggles.partSig) {
      const prow = resolvePartRow(partSeriesByDate, viewData, idx);
      const chips = buildPartHoverHtml(prow, { compact: false });
      if (chips) html += chips;
    }
    return html;
  }, [viewData, tf, foScores, eqScores, toggles, foReversal, eqReversal, eqCache, partSeriesByDate]);

  useEffect(() => {
    const idx = hoverIdx >= 0 ? hoverIdx : viewData.length - 1;
    if (viewData[idx]) setHoverHtml(buildHoverHtml(idx, viewData[idx]));
  }, [hoverIdx, viewData, buildHoverHtml]);

  const stats = useMemo(() => {
    if (!viewData.length) return null;
    const last = viewData[viewData.length - 1];
    const prev = viewData.length > 1 ? viewData[viewData.length - 2] : last;
    const first = viewData[0];
    const chg = last.close - prev.close;
    const chgP = prev.close ? ((chg / prev.close) * 100).toFixed(2) : 0;
    const tot = last.close - first.close;
    const totP = first.close ? ((tot / first.close) * 100).toFixed(2) : 0;
    const hi = Math.max(...viewData.map(d => d.high));
    const lo = Math.min(...viewData.filter(d => d.low > 0).map(d => d.low));
    const avgV = Math.round(viewData.reduce((s, d) => s + (d.volume || 0), 0) / viewData.length);
    return { last, chg, chgP, tot, totP, hi, lo, avgV };
  }, [viewData]);

  const chartHeight = 720;

  return (
    <div className="eod-chart-page">
      <div className="ic-bar">
        <label>
          SYMBOL
          <select value={symbol} onChange={e => onSymbolChange(e.target.value)}>
            {symbols.indices.length > 0 && (
              <optgroup label="INDICES">
                {symbols.indices.map(s => (
                  <option key={s.symbol} value={s.symbol}>{s.symbol}</option>
                ))}
              </optgroup>
            )}
            {symbols.stocks.length > 0 && (
              <optgroup label="STOCKS">
                {symbols.stocks.map(s => (
                  <option key={s.symbol} value={s.symbol}>{s.symbol}</option>
                ))}
              </optgroup>
            )}
          </select>
        </label>

        <div className="ic-sep" />

        <div className="ic-tf" title="Timeframe">
          {TFS.map(t => (
            <button key={t} type="button" className={`ic-tfbtn${tf === t ? ' active' : ''}`} onClick={() => onTf(t)}>{t}</button>
          ))}
        </div>

        <div className="ic-sep" />

        <div className="ic-periods">
          {PERIODS.map(p => (
            <button key={p} type="button" className={`ic-per${period === p ? ' active' : ''}`} onClick={() => onPeriod(p)}>{p}</button>
          ))}
        </div>

        <div className="ic-sep" />

        <label>FROM<input type="date" value={from} onChange={e => { setFrom(e.target.value); setPeriod(''); }} /></label>
        <label>TO<input type="date" value={to} onChange={e => { setTo(e.target.value); setPeriod(''); }} /></label>

        <div className="ic-sep" />
        <button type="button" className="ic-btn" disabled={!symbol || loading} onClick={loadChart}>
          <i className="bi bi-graph-up" /> LOAD
        </button>
      </div>

      {stats && (
        <div className="ic-stats">
          <div className="sp"><span className="sl">TF</span><span className="sv">{tf}</span></div>
          <div className="sp"><span className="sl">LAST</span><span className="sv">{(+stats.last.close).toFixed(2)}</span></div>
          <div className="sp"><span className="sl">CHG</span><span className={`sv ${stats.chg >= 0 ? 'sg' : 'sr'}`}>{stats.chg >= 0 ? '+' : ''}{stats.chg.toFixed(2)} ({stats.chgP}%)</span></div>
          <div className="sp"><span className="sl">PERIOD</span><span className={`sv ${stats.tot >= 0 ? 'sg' : 'sr'}`}>{stats.tot >= 0 ? '+' : ''}{stats.tot.toFixed(2)} ({stats.totP}%)</span></div>
          <div className="sp"><span className="sl">HIGH</span><span className="sv sg">{(+stats.hi).toFixed(2)}</span></div>
          <div className="sp"><span className="sl">LOW</span><span className="sv sr">{(+stats.lo).toFixed(2)}</span></div>
          <div className="sp"><span className="sl">AVG VOL</span><span className="sv">{fmtVol(stats.avgV)}</span></div>
          <div className="sp"><span className="sl">BARS</span><span className="sv">{viewData.length}</span></div>
          {stats.last.pe != null && <div className="sp"><span className="sl">P/E</span><span className="sv sa">{stats.last.pe}</span></div>}
          {stats.last.pb != null && <div className="sp"><span className="sl">P/B</span><span className="sv sa">{stats.last.pb}</span></div>}
        </div>
      )}

      <div className="ic-body">
        {loading && (
          <div className="ic-loading"><div className="ic-spin" /> Loading chart data…</div>
        )}
        {!loading && error && (
          <div className="ic-empty"><i className="bi bi-x-circle" style={{ fontSize: 18, color: 'var(--red)' }} /> {error}</div>
        )}
        {!loading && !error && !viewData.length && (
          <div className="ic-empty"><i className="bi bi-bar-chart-line" style={{ fontSize: 20 }} /> Select symbol and click LOAD</div>
        )}
        {!loading && !error && viewData.length > 0 && (
          <div className="ic-wrap">
            <div className="ic-ch-hdr">
              <span className="ic-sym">{meta.symbol || symbol}</span>
              <span className="ic-meta">{meta.exch || 'NSE'} · {tf} · {viewData.length} bars · {viewData[0]?.date} → {viewData[viewData.length - 1]?.date}</span>
              <div className="ic-ohlc" dangerouslySetInnerHTML={{ __html: hoverHtml }} />
              <div className="ic-trow">
                {TOGGLE_BTNS.map(({ k, label, title }) => (
                  <button
                    key={k}
                    type="button"
                    className={`ic-tgl${toggles[k] ? ' on' : ''}`}
                    title={title}
                    onClick={() => onToggle(k)}
                  >{label}</button>
                ))}
              </div>
            </div>

            <div className="ic-pnl ic-candle">
              <span className="ic-pnl-lbl">OHLC · FO · EQ · PART</span>
              <EodCandleChart
                data={viewData}
                fullData={fullData}
                toggles={toggles}
                foData={foCache}
                eqData={eqCache}
                partSignals={partSignals}
                partSeriesByDate={partSeriesByDate}
                cprMonthly={cprMonthly}
                cprWeekly={cprWeekly}
                monthStarts={markers.monthStarts || []}
                foReversal={foReversal}
                eqReversal={eqReversal}
                height={chartHeight}
                onHoverIdx={setHoverIdx}
                onCrosshair={(idx) => setHoverIdx(idx)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}